import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserPlus, Copy, KeyRound, Link2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useVocab } from "@/lib/vocab";
import { roleLabel } from "@/lib/rights";
import { toast } from "sonner";

const STATUS_LABEL = { invited: "Invité", active: "Actif", disabled: "Désactivé" };

export default function Users() {
  const { user } = useAuth();
  const voc = useVocab();
  // Rôle « Dispatcheur » (lot T5-0) : transport seulement ; en conciergerie, listes identiques à avant.
  const transport = voc.business === "transport";
  const [users, setUsers] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "employee" });
  const [link, setLink] = useState("");
  const [linkDialog, setLinkDialog] = useState({ open: false });

  const load = useCallback(() => api.get("/users").then((r) => setUsers(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const invite = async () => {
    if (!form.name || !form.email) { toast.error("Nom et email requis"); return; }
    try {
      const { data } = await api.post("/auth/invite", form);
      setLink(data.activation_link);
      if (data.email_sent) toast.success("Invitation envoyée par email");
      else toast.success("Invitation créée — copiez le lien ci-dessous et envoyez-le (WhatsApp…)");
      setForm({ name: "", email: "", role: "employee" });
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const changeRole = async (id, role) => { try { await api.patch(`/users/${id}`, { role }); load(); toast.success("Rôle mis à jour"); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const toggleStatus = async (u) => { try { await api.patch(`/users/${u.id}`, { status: u.status === "disabled" ? "active" : "disabled" }); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  // Lien à copier et envoyer soi-même (WhatsApp…) : réinitialisation (compte actif) ou nouvelle invitation (compte invité).
  const makeLink = async (u, kind) => {
    try {
      const { data } = await api.post(`/users/${u.id}/${kind === "reset" ? "reset-link" : "invite-link"}`);
      setLinkDialog({ open: true, kind, name: u.name, link: data.link, validity: data.validity });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  // Un lien donne accès au compte : seul un Master peut en créer pour un Master (même règle côté serveur).
  const canLink = (u) => u.role !== "master" || user.role === "master";
  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copié"); }
    catch { toast.error("Copie impossible : sélectionnez le lien et copiez-le à la main"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-overline text-accent">Équipe</div>
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight mt-1">Utilisateurs</h1>
        </div>
        <Button className="gap-2" onClick={() => { setLink(""); setDialog(true); }} data-testid="invite-button"><UserPlus className="h-4 w-4" /> Inviter</Button>
      </div>

      <div className="bg-card border border-border rounded-2xl shadow-card overflow-hidden divide-y divide-border">
        {users.map((u) => (
          <div key={u.id} className="p-4 md:p-5 flex flex-wrap items-center gap-4" data-testid={`user-row-${u.id}`}>
            <div className="h-10 w-10 rounded-full bg-accent/20 grid place-items-center font-semibold uppercase">{u.name?.[0]}</div>
            {/* Sur téléphone : nom et e-mail à côté de l'initiale (toute la ligne moins l'initiale et l'espace) ; le reste dessous. */}
            <div className="min-w-0 flex-1 max-md:basis-[calc(100%-3.5rem)]">
              <div className="font-medium">{u.name} {u.role === "master" && <span className="text-xs text-accent">· Propriétaire</span>}</div>
              <div className="text-sm text-muted-foreground">{u.email}</div>
            </div>
            <span className={cn("px-2.5 py-1 text-xs rounded-full", u.status === "active" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" : u.status === "invited" ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400" : "bg-muted text-muted-foreground")}>{STATUS_LABEL[u.status]}</span>
            {u.role === "master" ? (
              <span className="text-sm font-medium w-32 text-center">{roleLabel(u.role, voc)}</span>
            ) : (
              <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)}>
                <SelectTrigger className="w-32" data-testid={`role-select-${u.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>{transport && <SelectItem value="dispatcher">Dispatcheur</SelectItem>}<SelectItem value="secretary">Secrétaire</SelectItem><SelectItem value="employee">{voc.Employee}</SelectItem></SelectContent>
              </Select>
            )}
            {u.status === "active" && u.id !== user.id && canLink(u) && <Button size="sm" variant="outline" className="gap-1.5" onClick={() => makeLink(u, "reset")} data-testid={`reset-link-${u.id}`}><KeyRound className="h-3.5 w-3.5" /> Lien de réinitialisation</Button>}
            {u.status === "invited" && canLink(u) && <Button size="sm" variant="outline" className="gap-1.5" onClick={() => makeLink(u, "invite")} data-testid={`invite-link-${u.id}`}><Link2 className="h-3.5 w-3.5" /> Nouveau lien d'invitation</Button>}
            {u.role !== "master" && <Button size="sm" variant="outline" onClick={() => toggleStatus(u)} data-testid={`toggle-status-${u.id}`}>{u.status === "disabled" ? "Réactiver" : "Désactiver"}</Button>}
          </div>
        ))}
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display">Inviter un utilisateur</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Nom</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="invite-name" /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="invite-email" /></div>
            <div className="space-y-1.5"><Label>Rôle</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="invite-role"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="employee">{voc.Employee}</SelectItem><SelectItem value="secretary">Secrétaire</SelectItem>{transport && <SelectItem value="dispatcher">Dispatcheur</SelectItem>}{user.role === "master" && <SelectItem value="master">Patron</SelectItem>}</SelectContent>
              </Select>
            </div>
            {link && (
              <div className="p-3 bg-muted text-xs break-all flex items-center gap-2" data-testid="invite-link">
                <span className="flex-1">{link}</span>
                <button onClick={() => copy(link)}><Copy className="h-4 w-4" /></button>
              </div>
            )}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDialog(false)}>Fermer</Button><Button onClick={invite} data-testid="invite-submit">Envoyer l'invitation</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={linkDialog.open} onOpenChange={(o) => setLinkDialog(o ? linkDialog : { open: false })}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display">{linkDialog.kind === "reset" ? "Lien de réinitialisation" : "Nouveau lien d'invitation"}</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-muted-foreground">
              {linkDialog.kind === "reset"
                ? <>Envoyez ce lien à <b className="text-foreground">{linkDialog.name}</b> (WhatsApp…) : il permet de choisir un nouveau mot de passe. Valable <b className="text-foreground">{linkDialog.validity}</b>, utilisable une seule fois. Les liens envoyés avant ne marchent plus.</>
                : <>Envoyez ce lien à <b className="text-foreground">{linkDialog.name}</b> (WhatsApp…) pour activer son compte. Valable <b className="text-foreground">{linkDialog.validity}</b>. L'ancien lien d'invitation ne marche plus.</>}
            </p>
            <div className="p-3 bg-muted text-xs break-all flex items-center gap-2" data-testid="user-link">
              <span className="flex-1 select-all">{linkDialog.link}</span>
              <button onClick={() => copy(linkDialog.link)} title="Copier" data-testid="user-link-copy"><Copy className="h-4 w-4" /></button>
            </div>
          </div>
          <DialogFooter><Button onClick={() => copy(linkDialog.link)} className="gap-1.5"><Copy className="h-4 w-4" /> Copier le lien</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
